import { Col, Layout, Row, Space, theme, Typography } from 'antd';
import { observer } from 'mobx-react-lite';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PublicVisibleType, PublicVisibleTypeMap } from '../../../../../types/form';
import ActionAfterSubmitMenu from '../../../../components/ActionAfterSubmitMenu';
import BreadCrumbs from '../../../../components/BreadCrumbs';
import { customFieldTypeMap } from '../../../../components/CustomField/CustomField.type';
import EditTicketForm from '../../../../components/Forms/EditTicketForm';
import MacroButton from '../../../../components/MacroPage/MacroButton';
import SubmitButton from '../../../../components/SubmitButton';
import { isChannelGSocial } from '../../../../components/TicketPage/CommentTypeSelect';
import TicketActionButton from '../../../../components/TicketPage/TicketActionButton';
import { editTicketDefaultValues } from '../../../../constants/forms/ticketForm';
import CommentContainer from '../../../../containers/Pages/TicketPages/TicketPagesContentLayout/TicketDetailContentLayout/CommentContainer';
import TicketInfoLeftSideTabs from '../../../../containers/Pages/TicketPages/TicketPagesContentLayout/TicketDetailContentLayout/TicketInfoLeftSideTabs';
import { EditTicketSubjectContainer } from '../../../../containers/Pages/TicketPages/TicketPagesContentLayout/TicketDetailContentLayout/TicketInfoLeftSideTabs/SubjectContainer';
import TicketInteractivityContainer from '../../../../containers/Pages/TicketPages/TicketPagesContentLayout/TicketDetailContentLayout/TicketInteractivityContainer';
import useSingleCompanySetting from '../../../../hooks/adminPage/useSingleCompanySetting';
import useCurrentTicket from '../../../../hooks/ticketDetailPage/useCurrentTicket';
import useCurrentTicketStatus from '../../../../hooks/ticketDetailPage/useCurrentTicketStatus';
import useFormName from '../../../../hooks/ticketDetailPage/useFormName';
import useTicketFieldDefinitionList from '../../../../hooks/ticketDetailPage/useTicketFieldDefinitionList';
import {
    channelTypeMap,
    Comment,
    DetailedUser,
    NewTicket,
    settingsKey,
    statusOptions,
    Ticket,
    TICKET_FORM_FIELDS_KEY,
    ticketInteractionViewOptionsMap,
    ticketInteractionViewViewType,
    TicketSystemFieldsKey
} from '../../../../model/types';
import { useCurrentUserStore, useMacroStore } from '../../../../store/RootStoreContext';
import { grispiNotification } from '../../../../store/useNotificationStore';
import {
    MAIN_SIDEBAR_WIDTH,
    MIN_WIDTH,
    TAB_BAR_HEIGHT,
    TICKET_FOOTER_HEIGHT,
    TICKET_SIDEBAR_WIDTH
} from '../../../../styles/theme';
import { getUserFriendlyValueFromUser } from '../../../../utils/forms/formFieldsHelper.values';
import { publicVisibleToBooleanConverter } from '../../../../utils/forms/formHelper.parser';
import { by } from '../../../../utils/helpers';
import { redirectTo } from '../../../../utils/routeHelper';
import { getUtilityStore } from '../../../../utils/store/getStore';
import LoadingTicketPageLayout from '../LoadingTicketPageLayout';

const { Content, Footer, Sider } = Layout;

export enum BreadCrumbsViewState {
    organizationView = '0',
    userView = '1',
    ticketView = '2'
}

export const commentsViewTypeMap = {
    [channelTypeMap.CALL]: ticketInteractionViewOptionsMap.conversations,
    [channelTypeMap.CHAT]: ticketInteractionViewOptionsMap.chat,
    [channelTypeMap.EMAIL]: ticketInteractionViewOptionsMap.conversations,
    [channelTypeMap.INTEGRATION]: ticketInteractionViewOptionsMap.conversations,
    [channelTypeMap.IMPORT]: ticketInteractionViewOptionsMap.conversations,
    [channelTypeMap.SCHEDULER]: ticketInteractionViewOptionsMap.conversations,
    [channelTypeMap.TRIGGER]: ticketInteractionViewOptionsMap.conversations,
    [channelTypeMap.WEB]: ticketInteractionViewOptionsMap.conversations,
    [channelTypeMap.OTHER]: ticketInteractionViewOptionsMap.conversations,
    [channelTypeMap.USER_FORM]: ticketInteractionViewOptionsMap.conversations,
    [channelTypeMap.APPLE_APP_STORE]: ticketInteractionViewOptionsMap.conversations,
    [channelTypeMap.FACEBOOK]: ticketInteractionViewOptionsMap.conversations,
    [channelTypeMap.GOOGLE_PLAY_STORE]: ticketInteractionViewOptionsMap.conversations,
    [channelTypeMap.GOOGLE_REVIEWS]: ticketInteractionViewOptionsMap.conversations,
    [channelTypeMap.INSTAGRAM]: ticketInteractionViewOptionsMap.gsocialChat,
    [channelTypeMap.INSTAGRAM_DM]: ticketInteractionViewOptionsMap.gsocialChat,
    [channelTypeMap.INSTAGRAM_COMMENT]: ticketInteractionViewOptionsMap.gsocialChat,
    [channelTypeMap.SIKAYET_VAR]: ticketInteractionViewOptionsMap.conversations,
    [channelTypeMap.TWITTER]: ticketInteractionViewOptionsMap.gsocialChat,
    [channelTypeMap.WHATSAPP]: ticketInteractionViewOptionsMap.gsocialChat
} as const;

const MemoizedMacroButton = memo(MacroButton, (prevProps, nextProps) => prevProps.viewType === nextProps.viewType);
// const MemoizedTicketFormEventListener = memo(TicketFormEventListener);
export const convertTicketFieldsDataToFormValues = (ticketDetail: NewTicket) => {
    return {
        ...editTicketDefaultValues[TICKET_FORM_FIELDS_KEY],
        ...Object.fromEntries(
            Object.entries(ticketDetail?.fieldMap ?? {})
                .filter(([fieldKey]) => fieldKey !== TicketSystemFieldsKey.creator) // will not send ts.creator info again
                .map(([fieldKey, { serializedValue, type, userFriendlyValue, value }]) => {
                    const hookFormFriendlyFieldKey = fieldKey.replace(new RegExp('\\.', 'gi'), '$');

                    if (isChannelGSocial(ticketDetail?.channel) && fieldKey === TicketSystemFieldsKey.gSocial)
                        return [hookFormFriendlyFieldKey, serializedValue];

                    // HACK: This is due to fact that we cannot know if this field value is number or integer, all we know that `ts.form` is integer
                    if (fieldKey === TicketSystemFieldsKey.formId) return [hookFormFriendlyFieldKey, +serializedValue];
                    if (fieldKey === TicketSystemFieldsKey.status)
                        return [hookFormFriendlyFieldKey, (value as Ticket['status']).id];

                    if (!serializedValue) return [hookFormFriendlyFieldKey, null as string | number];
                    if (
                        [customFieldTypeMap.USER, customFieldTypeMap.UNTRUSTED_END_USER].includes(
                            type as 'USER' | 'UNTRUSTED_END_USER'
                        )
                    ) {
                        if (!userFriendlyValue) {
                            // console.debug(
                            //     `TicketDetailContentLayout , defaultFormValues , userFriendlyValue does not exist for field with fieldKey:`,
                            //     fieldKey
                            // );
                            // console.debug(
                            //     `will cause userFriendlyValue to be shown on UI as:`,
                            //     serializedValue
                            // );
                            return [
                                hookFormFriendlyFieldKey,
                                `${serializedValue}|${
                                    getUserFriendlyValueFromUser(value as DetailedUser) ??
                                    (value as DetailedUser)?.phone
                                }`
                            ];
                        }
                        return [hookFormFriendlyFieldKey, `${serializedValue}|${userFriendlyValue}`];
                    }

                    if (type === customFieldTypeMap.MULTI_USER) {
                        if (!userFriendlyValue) {
                            // console.debug(
                            //     `TicketDetailContentLayout , defaultFormValues , userFriendlyValue does not exist for field with fieldKey:`,
                            //     fieldKey
                            // );
                            // console.debug(
                            //     `will cause userFriendlyValue to be shown on UI as:`,
                            //     serializedValue
                            // );
                            return [hookFormFriendlyFieldKey, `${serializedValue}|${serializedValue}`];
                        }
                        const userFriendlyValueList = userFriendlyValue.split(', ');
                        const hookFormFriendlyFieldValue = serializedValue
                            .split(',')
                            .map((sv, i) => `${sv}|${userFriendlyValueList[i]}`)
                            .toString();
                        return [hookFormFriendlyFieldKey, hookFormFriendlyFieldValue];
                    }

                    if (type === customFieldTypeMap.SELECT) {
                        return [hookFormFriendlyFieldKey, (value as { id: number })?.id?.toString()];
                    }

                    return [hookFormFriendlyFieldKey, serializedValue];
                })
        )
    };
};
const TicketDetailContentLayout = () => {
    const {
        token: { colorBgLayout, colorSplit, lineWidth, padding, paddingMD, paddingSM }
    } = theme.useToken();

    const { t } = useTranslation('tickets');
    const { t: tCommon } = useTranslation('common');
    const { email } = useCurrentUserStore();
    const { asPath } = useRouter();
    const formName = useFormName();
    const {
        data: {
            userInfo: { userId: id }
        }
    } = useSession();
    const { initPlaceholderValues } = useMacroStore();

    const { data: ticketDetail, error, isLoading: isLoadingTicketDetail } = useCurrentTicket();

    const { data: activeFields } = useTicketFieldDefinitionList(true);
    const { data } = useTicketFieldDefinitionList();

    const { data: conversationVisibility, isLoading: isCompanySettingsLoading } = useSingleCompanySetting(
        settingsKey.DEFAULT_CONVERSATION_VISIBILITY
    );
    const publicVisible = (conversationVisibility?.value?.toLowerCase() ??
        PublicVisibleTypeMap.public) as PublicVisibleType;

    const ticketStatus = useCurrentTicketStatus();

    const [commentCollapseActiveKey, setCommentCollapsedActiveKey] = useState(['1']);
    const [commentsViewType, setCommentsViewType] = useState<ticketInteractionViewViewType>(commentsViewTypeMap['WEB']);

    useEffect(() => {
        email && id && ticketDetail && initPlaceholderValues(ticketDetail);
    }, [email, id, initPlaceholderValues, ticketDetail]);

    useEffect(() => {
        ticketDetail && setCommentsViewType(commentsViewTypeMap[ticketDetail?.channel]);
    }, [ticketDetail]);

    useEffect(() => {
        if (error) {
            grispiNotification('warning', tCommon('noTicket'), asPath);
            redirectTo(getUtilityStore().lastVisitedFilterPageUrl);
        }
    }, [asPath, error, tCommon]);

    const defaultFormValues = useMemo(
        () => ({
            channel: ticketDetail?.channel ?? null,
            formName,
            ...editTicketDefaultValues,
            comment: {
                ...editTicketDefaultValues.comment,
                creator: [{ key: 'us.id' as any, value: id?.toString() }],
                ...(isChannelGSocial(ticketDetail?.channel)
                    ? {
                          channel: ticketDetail?.channel,
                          externalId: ticketDetail?.comments
                              ?.sort(by<Comment>('createdAt')('descending'))
                              ?.find(({ channel, externalId }) => channel === ticketDetail?.channel && externalId)
                              ?.externalId
                      } // assuming reply to last comment with existing externalId,
                    : {}),
                publicVisible: publicVisibleToBooleanConverter(publicVisible)
            },
            [TICKET_FORM_FIELDS_KEY]: convertTicketFieldsDataToFormValues(ticketDetail)
        }),
        [id, formName, publicVisible, ticketDetail]
    );

    if (isLoadingTicketDetail || isCompanySettingsLoading || !ticketDetail || !email || !activeFields || !data)
        return <LoadingTicketPageLayout />;

    const isClosedTicket = ticketStatus === statusOptions.Closed;

    const isConversationMode = commentsViewType === ticketInteractionViewOptionsMap.conversations;

    return (
        <EditTicketForm key={formName} defaultValues={defaultFormValues}>
            <Layout className="full-height" hasSider>
                <Sider
                    width={TICKET_SIDEBAR_WIDTH}
                    theme="light"
                    style={{
                        padding: paddingSM,
                        backgroundColor: colorBgLayout,
                        borderInlineEnd: `${lineWidth}px solid ${colorSplit}`,
                        height: `calc(100vh - ${TAB_BAR_HEIGHT}px)`,
                        overflowY: 'auto'
                    }}
                >
                    <BreadCrumbs viewOption={BreadCrumbsViewState.ticketView} />
                    <TicketInfoLeftSideTabs viewOption={BreadCrumbsViewState.ticketView} />
                </Sider>

                <Layout style={{ height: `calc(100% - ${TICKET_FOOTER_HEIGHT}px)` }}>
                    <Row
                        align="middle"
                        justify="space-between"
                        style={{
                            paddingBottom: paddingSM,
                            paddingLeft: paddingSM,
                            paddingTop: paddingMD,
                            paddingRight: 0,
                            borderBottom: `${lineWidth}px solid ${colorSplit}`
                        }}
                    >
                        <Col span={20}>
                            <EditTicketSubjectContainer />
                        </Col>
                        <Col span={2}>
                            <TicketActionButton />
                        </Col>
                        <Col span={2} />
                    </Row>
                    <Content
                        style={{
                            overflow: 'auto',
                            paddingBottom: paddingSM,
                            paddingLeft: paddingSM,
                            paddingRight: paddingSM,
                            paddingTop: 0,
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <TicketInteractivityContainer
                            commentsViewType={commentsViewType}
                            setCommentsViewType={setCommentsViewType}
                        />
                    </Content>
                    {isClosedTicket ? null : (
                        <Footer
                            style={{
                                display: !isConversationMode ? 'none' : 'block',
                                padding: 0
                            }}
                        >
                            <CommentContainer
                                commentCollapseActiveKey={commentCollapseActiveKey}
                                toggleComment={setCommentCollapsedActiveKey}
                                setCommentsViewType={setCommentsViewType}
                            />
                        </Footer>
                    )}
                    <Footer
                        style={{
                            bottom: 0,
                            height: TICKET_FOOTER_HEIGHT,
                            paddingBottom: paddingSM,
                            paddingLeft: padding,
                            paddingRight: padding,
                            paddingTop: paddingSM,
                            position: 'absolute',
                            minWidth: `calc(${MIN_WIDTH}px - ${TICKET_SIDEBAR_WIDTH}px - ${MAIN_SIDEBAR_WIDTH}px)`,
                            width: `calc(100% - ${TICKET_SIDEBAR_WIDTH}px - ${MAIN_SIDEBAR_WIDTH}px)`
                        }}
                    >
                        <Row justify="space-between" wrap={false}>
                            <Col>{!isClosedTicket && <MemoizedMacroButton viewType={commentsViewType} />}</Col>
                            <Space size="middle">
                                {isClosedTicket ? (
                                    <Typography.Text strong>{t('ticketClosed')}</Typography.Text>
                                ) : (
                                    <ActionAfterSubmitMenu />
                                )}
                                <SubmitButton viewOption={BreadCrumbsViewState.ticketView} />
                            </Space>
                        </Row>
                    </Footer>
                </Layout>
            </Layout>
        </EditTicketForm>
    );
};

export default observer(TicketDetailContentLayout);
